import featureAPI from '@/services/feature-api';
import { isEqual, isNil } from 'lodash';

import { buildPdfMatrix, loadImage, hexToRgb, generatePdfFilename } from './pdf';
export { buildPdfMatrix, loadImage, hexToRgb, generatePdfFilename };

export function formatStringDate(stringDate) {
  const date = new Date(stringDate);
  if (date instanceof Date && !isNaN(date.valueOf())) {
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }
  return stringDate;
}


export const statusChoices = [
  {
    name: 'Brouillon',
    value: 'draft',
  },
  {
    name: 'En attente de publication',
    value: 'pending',
  },
  {
    name: 'Publié',
    value: 'published',
  },
  {
    name: 'Archivé',
    value: 'archived',
  },
];

export function allowedStatus2change(user, isModerate, userLevel, isOwnFeature, currentRouteName) {
  if ( //* si 'super-admin'(superuser) admin, modérateur ou super contributeur, statuts toujours disponibles: Brouillon, Publié, Archivé
    user.is_superuser ||
      userLevel === 'Modérateur' ||
      userLevel === 'Administrateur projet' ||
      (userLevel === 'Super Contributeur' && !isModerate)
  ) {
    return statusChoices.filter((el) => el.value !== 'pending');
  } else if (userLevel === 'Super Contributeur' && isModerate) {
    return statusChoices.filter(
      (el) => el.value === 'draft' || el.value === 'pending'
    );
  } else if (userLevel === 'Contributeur') { //* cas particuliers du contributeur
    if (currentRouteName === 'ajouter-signalement' || !isOwnFeature) {
      //* même cas à l'ajout d'une feature ou si feature n'a pas été créé par le contributeur
      //? Incohérence / Inutile ? Un contributeur ne peut pas modifier un signalement d'un autre utilisateur
      return isModerate
        ? statusChoices.filter(
          (el) => el.value === 'draft' || el.value === 'pending'
        )
        : statusChoices.filter(
          (el) => el.value === 'draft' || el.value === 'published'
        );
    } else {
      //* à l'édition d'une feature et si le contributeur est l'auteur de la feature
      return isModerate
        ? statusChoices.filter(
          (el) => el.value !== 'published' //* toutes sauf "Publié"
        )
        : statusChoices.filter(
          (el) => el.value !== 'pending' //* toutes sauf "En cours de publication"
        );
    }
  }
  return [];
}

/**
 * Determines the type of a property based on its value.
 *
 * This function inspects a given property and returns a string indicating its type,
 * such as 'boolean', 'integer', 'decimal', 'date', 'text', or 'char'.
 * It uses various checks to determine the appropriate type for different value formats.
 *
 * @param {any} prop - The property value to be evaluated.
 * @returns {string} The determined type of the property ('boolean', 'integer', 'decimal', 'date', 'text', or 'char').
 */
export function transformProperties(prop) {
  const type = typeof prop;
  const regInteger = /^-?\d+$/;  // Regular expression to match integer numbers
  const regFloat = /^-?\d*\.\d+$/;  // Regular expression to match decimal numbers
  const regText = /[\r\n]/;  // Regular expression to detect multiline text (newlines)
  const regDate = /^\d{4}-\d{2}-\d{2}$|^\d{2}\/\d{2}\/\d{4}$/; // Regular expression to match common date formats

  // Check if the property is a boolean or a string that represents a boolean
  if (type === 'boolean' || (type === 'string' && (prop.toLowerCase() === 'true' || prop.toLowerCase() === 'false'))) {
    return 'boolean';
  } else if (regInteger.test(prop) || (type === 'number' && Number.isSafeInteger(prop))) {
    // Check if the property is an integer or a string that represents an integer
    return 'integer';
  } else if (type === 'string' && regDate.test(prop.trim())) {
    // More specific check for date strings using regular expressions
    return 'date';
  } else if (regFloat.test(prop) || (type === 'number' && !Number.isSafeInteger(prop))) {
    // Check if the property is a decimal number or a string that represents a decimal
    return 'decimal';
  } else if (regText.test(prop) || (type === 'string' && prop.length > 255)) {
    // Check if the property contains newline characters or is a long text
    return 'text';
  }

  // Default case for all other types: assume it is a short text or character field
  return 'char';
}


export function objIsEmpty(obj) {
  for(const prop in obj) {
    if(Object.hasOwn(obj, prop)) {
      return false;
    }
  }

  return true;
}

export const reservedKeywords = [
  // todo : add keywords for mapstyle (strokewidth...)
  'id',
  'title',
  'description',
  'status',
  'created_on',
  'updated_on',
  'archived_on',
  'deletion_on',
  'feature_type',
  'feature_id',
  'display_creator',
  'display_last_editor',
  'project',
  'creator',
  'lat',
  'lon'
];

export const customFieldTypeChoices = [
  { name: 'Booléen', value: 'boolean' },
  { name: 'Chaîne de caractères', value: 'char' },
  { name: 'Date', value: 'date' },
  { name: 'Liste de valeurs', value: 'list' },
  { name: 'Liste de valeurs pré-enregistrées', value: 'pre_recorded_list' },
  { name: 'Liste à choix multiples', value: 'multi_choices_list' },
  { name: 'Nombre entier', value: 'integer' },
  { name: 'Nombre décimal', value: 'decimal' },
  { name: 'Texte multiligne', value: 'text' },
  { name: 'Notification à un groupe', value: 'notif_group' },
];

export const featureNativeFields = [
  { name: 'status', label: 'Statut', field_type: 'Champ GéoContrib' },
  { name: 'feature_type', label: 'Type', field_type: 'Champ GéoContrib' },
  { name: 'updated_on', label: 'Dernière mise à jour', field_type: 'Champ GéoContrib' },
  { name: 'created_on', label: 'Date de création', field_type: 'Champ GéoContrib' },
  { name: 'display_creator', label: 'Auteur', field_type: 'Champ GéoContrib' },
  { name: 'display_last_editor', label: 'Dernier éditeur', field_type: 'Champ GéoContrib' },
];

export const retrieveFeatureProperties = async (feature, featureTypes, projectSlug) => {
  const properties = feature.getProperties();
  let {
    feature_type,
    status,
    updated_on,
    created_on,
    creator,
    display_last_editor,
    index,
    attachments_images
  } = properties;

  if (creator) { 
    creator = creator.full_name ? `${creator.first_name} ${creator.last_name}` : creator.username;
  } else if (properties.feature_id) {
    //* if *** MVT *** feature, retrieve display_creator and display_last_editor by fetching the feature details from API
    const fetchedFeature = await featureAPI.getProjectFeature(projectSlug, properties.feature_id, 'geojson');
    if (fetchedFeature) {
      creator = fetchedFeature.properties.display_creator;
      display_last_editor = fetchedFeature.properties.display_last_editor;
      feature_type = fetchedFeature.properties.feature_type;
      attachments_images = fetchedFeature.properties.attachments_images;
    }
  }
  
  if (featureTypes && feature_type) {
    feature_type = featureTypes.find((el) => el.slug === (feature_type.slug || feature_type));
  }
  // Format dates
  updated_on = formatStringDate(updated_on || '');
  created_on = formatStringDate(created_on || '');
  
  if (status) {
    if (status.label) { //* when the label is already in the feature
      status = status.label;
    } else if (featureTypes) { //* if not, retrieve the name/label from the list
      status = statusChoices.find((el) => el.value === status).name;
    }
  }

  return { feature_type, status, updated_on, created_on, creator, display_last_editor, index, attachments_images };
};

export function findXformValue(feature, customField) {
  if (!feature) return null;
  if (feature.properties) {
    return feature.properties[customField.name] || null;
  } else if (feature.feature_data) {
    const field = feature.feature_data.find((el) => el.label === customField.label);
    return field ? field.value : null;
  }
  return null;
}

export function isXtraFormActive(extraForms, config) { // return true if no config or if the condition is fullfilled
  if (config) { // if conditional field configuration is not null
    // get name and value in condition
    const { conditionField, conditionValue } = config;
    // get the customForm which activates conditional field
    const conditioningXForm = extraForms.find((xForm) => xForm.name === conditionField);
    // check if the conditioning extraform value match the condition value
    if (conditioningXForm) {
      // if the values to compare are null or undefined the field can't be activated
      if (isNil(conditioningXForm.value) || isNil(conditionValue)) {
        return false;
      } else if (Array.isArray(conditionValue) && Array.isArray(conditioningXForm.value)) { // case of multiple list or prerecorded values list
        return conditioningXForm.value.some((value) => conditionValue.includes(value));
      } else if (typeof conditioningXForm.value === 'object' && conditioningXForm.value.label) { // case of simple list
        return conditioningXForm.value.label === conditionValue.label;
      } else {
        return conditioningXForm.value === conditionValue; // more simple case of other fields
      } 
    }
  }
  return true;
}

export function checkDeactivatedValues(extraForms) {
  // if changes occured, update extraForms array with freshly checked active customForms
  let newExtraForms = extraForms.map((xForm) => { // we use 'deactivate' instead of 'activate' because at initialization this property cannot be evaluated ...
    const isDeactivated = !isXtraFormActive(extraForms, xForm.conditional_field_config);  // ... if the component is not created to set this property, thus no extra form would appear at all
    // toggle value to null to deactivate other fields conditioned by it
    if (isDeactivated) {
      xForm['value'] = null;
    }
    return { ...xForm, ['isDeactivated']: isDeactivated };
  });
  return newExtraForms;
}

export function checkFieldForcedValue(field, extraForms) {
  field['disabled'] = false; //* create a property disabled and (re)set to false by default
  if (field.forced_value_config) {
    //* loop over each forced value config for this extraForm
    for (const config of field.forced_value_config) {
      //* find the extraForm field conditioning the forced value
      const conditioningField = extraForms.find((xtraForm) => xtraForm.name === config.conditionField);
      //* if found check that its value match the condtionValue
      if (conditioningField && isEqual(conditioningField.value, config.conditionValue)) {
        //* set this value with the forced value and disable the form field
        field.value = config.forcedValue;
        field.disabled = true;
      }
    }
  }
  return field;
}

export function activateFieldsNforceValues(extraForms) {
  for (const [index, field] of extraForms.entries()) {
    const checkedField = checkFieldForcedValue(field, extraForms);
    //* each time a value changes, call this function recursively, until there is no more change
    if (checkedField.value !== field.value) {
      extraForms[index] = checkedField; //* update the value in extraForms
      activateFieldsNforceValues(extraForms); //* call the function with new extraForms
    }
  }
  //* when no more changes detected in the loop, check for deactivated extraForms
  extraForms = checkDeactivatedValues(extraForms);
  //* return extraForms from the lastly called function
  return extraForms;
}

export function formatUserOption(user) {
  let name = user.first_name || '';
  if (user.last_name) {
    name = name + ' ' + user.last_name;
  }
  return {
    name: [name, user.username],
    value: user.id,
  };
}

/**
 * Vérifie si l'utilisateur courant est le créateur d'une entité (feature).
 *
 * Cette fonction compare l'utilisateur connecté avec le créateur d'une feature.
 * Elle gère les cas où `creator` peut être soit un identifiant, soit un objet utilisateur,
 * et évite toute erreur si la propriété `creator` est nulle ou absente.
 *
 * @param {Object} currentFeature - L'objet représentant la feature (avec une propriété `properties.creator`).
 * @param {Object} user - L'utilisateur courant (avec les propriétés `id` et `username`).
 * @returns {boolean} - `true` si l'utilisateur est le créateur, sinon `false`.
 */
export function isFeatureCreator(currentFeature, user) {
  const creator = currentFeature?.properties?.creator;

  if (!creator || !user) {
    return false;
  }

  return (
    creator === user.id ||
    creator?.username === user.username
  );
}

/**
 *  Return the class to display corresponding icon
 * 
 * @param {String} strStatus - The status value as a string
 * @returns {String} - The corresponding class name
 */
export function getStatusIconClass(strStatus) {
  switch (strStatus) {
  case 'archived':
    return 'grey archive';
  case 'pending':
    return 'teal hourglass outline';
  case 'published':
    return 'olive check';
  case 'draft':
    return 'orange pencil alternate';
  default:
    return '';
  }
}

/**
 * Vérifie si l'utilisateur a le droit de supprimer une feature.
 * @param {Object} feature - La feature à vérifier.
 * @param {Object} user - L'utilisateur à l'origine de la suppression.
 * @param {String} userLevel - Le rôle de l'utilisateur dans ce projet.
 * @returns {Boolean} - `true` si l'utilisateur peut supprimer la feature.
*/
export function canDeleteFeature(feature, user, userLevel) {
  if (userLevel === 'Administrateur projet' || user.is_superuser) {
    return true; // Un administrateur ou super utilisateur peut tout supprimer
  }
  // Sinon, on ne peut supprimer que ses propres features
  return feature.creator === user.id;
}

/**
 * Vérifie si l'utilisateur a le droit de modifier une feature.
 * @param {Object} feature - La feature à vérifier.
 * @param {Object} user - L'utilisateur à l'origine de la modification.
 * @param {String} userLevel - Le rôle de l'utilisateur dans ce projet.
 * @returns {Boolean} - `true` si l'utilisateur peut modifier la feature.
 */
export function canEditFeature(feature, user, userLevel) {
  const statusEditPermissions = {
    'Administrateur projet' : ['draft', 'pending', 'published', 'archived'],
    Modérateur : ['draft', 'pending', 'published'],
    'Super Contributeur' : ['draft', 'pending', 'published'],
    Contributeur : ['draft', 'pending', 'published'],
  };

  if (user.is_superuser) {
    return true;
  } else if (userLevel === 'Contributeur' && feature.creator !== user.id) {
    return false;
  } else if (statusEditPermissions[userLevel]) {
    return statusEditPermissions[userLevel].includes(feature.status);
  } else {
    return false;
  }
}

/**
 * Transforms customFilters into query parameters compatible with the API.
 * @param {Array} customFilters - Array of custom filter objects.
 * @returns {Object} - Flat object with keys like `feature_data__<field_name>`.
 */
export function transformCustomFilters(customFilters) {
  const params = {};
  customFilters.forEach(filter => {
    if (filter.value !== null && filter.value !== undefined && filter.value !== '') {
      // Gestion des ranges (dates)
      if (filter.as_range && Array.isArray(filter.value)) {
        if (filter.value[0]) params[`${filter.name}__gte`] = filter.value[0];
        if (filter.value[1]) params[`${filter.name}__lte`] = filter.value[1];
      }
      else if (['char', 'text', 'integer', 'decimal'].includes(filter.field_type)) {
        params[`${filter.name}__icontains`] = filter.value;
      }
      else if (['multi_choices_list'].includes(filter.field_type)) {
        params[`${filter.name}__contains`] = filter.value;
      }
      // Gestion des champs simples (booléens, liste, etc.)
      else {
        params[filter.name] = filter.value;
      }
    }
  });
  return params;
}

export function handleApiError(error, displayMessage) {
  let message = 'Une erreur est survenue.';

  if (error.response?.data) {
    message = error.response.data.error || message;
  } else if (error.request) {
    console.error('Aucune réponse reçue :', error.request);
    message = 'Impossible de communiquer avec le serveur.';
  } else {
    console.error('Erreur de configuration :', error.message);
  }

  displayMessage({
    comment: message,
    level: 'negative',
  });
}

/**
 * Construit l'URL absolue de partage d'un projet.
 * L'application est servie sous une base (ex: /geocontrib/) qui doit être conservée dans le lien.
 *
 * @param {String} slug - Le slug du projet à partager.
 * @param {String} base - La base du router (this.$router.options.base).
 * @returns {String} - L'URL absolue vers la vue partagée du projet.
 */
export function buildSharedProjectUrl(slug, base = '/') {
  const rawBase = String(base || '');
  let start = 0;
  let end = rawBase.length;

  while (start < end && rawBase[start] === '/') start += 1;
  while (end > start && rawBase[end - 1] === '/') end -= 1;

  const prefix = rawBase.slice(start, end);
  return `${window.location.origin}/${prefix ? `${prefix}/` : ''}projet-partage/${slug}`;
}

export function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
